import { useRequiredContext } from "@redotech/react-util/context";
import { sinkPromise } from "@redotech/util/promise";
import { sleep } from "@redotech/util/schedule";
import { useEffect, useMemo, useRef, useState } from "react";
import { RedoMerchantRpcClientContext } from "../rpc-client";
import {
  ActiveEscalationSummary,
  EscalationConnectionState,
  EscalationStatus,
  useEscalationContext,
} from "./escalation-context";
import {
  humanMessageFromEvent,
  type HumanMessage,
} from "./use-escalation-subscription";

const INITIAL_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30_000;
const SLOW_RECONNECT_DELAY_MS = 60_000;
/** Attempts on the fast ramp before retries drop to the slow cadence. */
const MAX_RECONNECT_ATTEMPTS = 10;

interface EscalationConnection {
  abortController: AbortController;
  messageIds: Set<string>;
  reconnectAttempts: number;
  connecting: boolean;
  /** True once a stream has connected; distinguishes reconnects from the first connect. */
  everConnected: boolean;
  /** Guards the degraded callback to once per outage. */
  degradedReported: boolean;
}

export interface UseEscalationSubscriptionsResult {
  connectedEscalationIds: ReadonlySet<string>;
}

/**
 * One SSE stream per escalation in `activeEscalations`, each with its
 * own reconnect/backoff that never stops retrying; mirrors into the
 * bound state when ids match.
 */
export function useEscalationSubscriptions(
  onNewMessage?: (
    summary: ActiveEscalationSummary,
    message: HumanMessage,
  ) => void,
  onStatusChange?: (
    summary: ActiveEscalationSummary,
    status: EscalationStatus,
  ) => void,
  onReconnect?: (summary: ActiveEscalationSummary) => void,
  onDegraded?: (summary: ActiveEscalationSummary) => void,
): UseEscalationSubscriptionsResult {
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const {
    escalation,
    setEscalation,
    activeEscalations,
    updateActiveEscalation,
    removeActiveEscalation,
  } = useEscalationContext();

  const [connectedEscalationIds, setConnectedEscalationIds] = useState<
    ReadonlySet<string>
  >(new Set());

  const connectionsRef = useRef<Map<string, EscalationConnection>>(new Map());
  const unmountedRef = useRef(false);

  // Refs for callbacks + context values read inside the long-lived
  // stream loops, so a re-render doesn't force reconnection.
  const onNewMessageRef = useRef(onNewMessage);
  const onStatusChangeRef = useRef(onStatusChange);
  const onReconnectRef = useRef(onReconnect);
  const onDegradedRef = useRef(onDegraded);
  const boundEscalationIdRef = useRef(escalation.escalationId);
  const setEscalationRef = useRef(setEscalation);
  const updateActiveEscalationRef = useRef(updateActiveEscalation);
  const removeActiveEscalationRef = useRef(removeActiveEscalation);
  const activeEscalationsRef = useRef(activeEscalations);

  useEffect(() => {
    onNewMessageRef.current = onNewMessage;
    onStatusChangeRef.current = onStatusChange;
    onReconnectRef.current = onReconnect;
    onDegradedRef.current = onDegraded;
  }, [onNewMessage, onStatusChange, onReconnect, onDegraded]);
  useEffect(() => {
    boundEscalationIdRef.current = escalation.escalationId;
  }, [escalation.escalationId]);
  useEffect(() => {
    setEscalationRef.current = setEscalation;
    updateActiveEscalationRef.current = updateActiveEscalation;
    removeActiveEscalationRef.current = removeActiveEscalation;
    activeEscalationsRef.current = activeEscalations;
  }, [
    setEscalation,
    updateActiveEscalation,
    removeActiveEscalation,
    activeEscalations,
  ]);

  // Membership key so the subscribe effect re-runs only when the set of
  // live-stream-worthy escalations changes, not on timestamp updates.
  const subscribableIdsKey = useMemo(
    () =>
      [...activeEscalations.values()]
        .filter((s) => s.status === "waiting" || s.status === "human-active")
        .map((s) => s.escalationId)
        .sort()
        .join(","),
    [activeEscalations],
  );

  useEffect(() => {
    const subscribableIds = new Set(
      subscribableIdsKey ? subscribableIdsKey.split(",") : [],
    );
    const connections = connectionsRef.current;

    const markConnected = (escalationId: string, connected: boolean) => {
      setConnectedEscalationIds((prev) => {
        if (prev.has(escalationId) === connected) {
          return prev;
        }
        const next = new Set(prev);
        if (connected) {
          next.add(escalationId);
        } else {
          next.delete(escalationId);
        }
        return next;
      });
    };

    const stopConnection = (escalationId: string) => {
      const connection = connections.get(escalationId);
      if (connection) {
        connection.abortController.abort();
        connections.delete(escalationId);
      }
      markConnected(escalationId, false);
    };

    const summaryFor = (escalationId: string): ActiveEscalationSummary =>
      activeEscalationsRef.current.get(escalationId) ?? {
        escalationId,
        triageThreadId: "",
        status: "human-active",
        lastAdminMessageAt: null,
        lastReadAt: null,
      };

    const mirrorToBoundEscalation = (
      escalationId: string,
      update: {
        status?: EscalationStatus;
        lastAdminMessageAt?: number;
        connectionState?: EscalationConnectionState;
      },
    ) => {
      if (boundEscalationIdRef.current === escalationId) {
        setEscalationRef.current(update);
      }
    };

    const connectLoop = async (escalationId: string) => {
      const connection = connections.get(escalationId);
      if (!connection || connection.abortController.signal.aborted) {
        return;
      }
      if (connection.connecting) {
        return;
      }
      connection.connecting = true;

      const signal = connection.abortController.signal;
      try {
        const streamGenerator = merchantRpcClient.watchEscalationEvents(
          { escalationId },
          { signal },
        );

        for await (const event of streamGenerator) {
          if (signal.aborted) {
            break;
          }

          switch (event.type) {
            case "connection-made": {
              markConnected(escalationId, true);
              const isReconnect = connection.everConnected;
              connection.everConnected = true;
              connection.reconnectAttempts = 0;
              connection.degradedReported = false;
              mirrorToBoundEscalation(escalationId, {
                connectionState: "connected",
              });
              // Only a re-connect can have a message gap to catch up on;
              // the initial connect is covered by the cold-start backfill.
              if (isReconnect) {
                onReconnectRef.current?.(summaryFor(escalationId));
              }
              break;
            }

            case "human-message": {
              const message = humanMessageFromEvent(event);
              if (!message) {
                break;
              }
              if (connection.messageIds.has(message.id)) {
                break;
              }
              connection.messageIds.add(message.id);

              const lastAdminMessageAt = Math.round(message.createdAt * 1000);
              updateActiveEscalationRef.current(escalationId, {
                status: "human-active",
                lastAdminMessageAt,
              });
              mirrorToBoundEscalation(escalationId, {
                status: "human-active",
                lastAdminMessageAt,
              });
              onNewMessageRef.current?.(summaryFor(escalationId), message);
              break;
            }

            case "status-change": {
              if (!event.status) {
                break;
              }
              updateActiveEscalationRef.current(escalationId, {
                status: event.status,
              });
              mirrorToBoundEscalation(escalationId, { status: event.status });
              onStatusChangeRef.current?.(
                summaryFor(escalationId),
                event.status,
              );
              break;
            }

            case "escalation-closed": {
              const summary = summaryFor(escalationId);
              mirrorToBoundEscalation(escalationId, { status: "closed" });
              removeActiveEscalationRef.current(escalationId);
              onStatusChangeRef.current?.(summary, "closed");
              stopConnection(escalationId);
              return;
            }

            case "error":
              break;
          }
        }

        // Stream ended normally (server timeout) — reconnect promptly.
        connection.connecting = false;
        if (!signal.aborted && !unmountedRef.current) {
          connection.reconnectAttempts = 0;
          connection.degradedReported = false;
          await sleep(Temporal.Duration.from({ seconds: 1 }));
          sinkPromise(connectLoop(escalationId));
        }
      } catch (error) {
        connection.connecting = false;
        markConnected(escalationId, false);

        if (error instanceof Error && error.name === "AbortError") {
          return;
        }
        if (signal.aborted || unmountedRef.current) {
          return;
        }
        const degraded = connection.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS;
        mirrorToBoundEscalation(escalationId, {
          connectionState: degraded ? "disconnected" : "reconnecting",
        });
        if (degraded && !connection.degradedReported) {
          connection.degradedReported = true;
          onDegradedRef.current?.(summaryFor(escalationId));
        }

        const delay = degraded
          ? SLOW_RECONNECT_DELAY_MS
          : Math.min(
              INITIAL_RECONNECT_DELAY_MS * 2 ** connection.reconnectAttempts,
              MAX_RECONNECT_DELAY_MS,
            );
        connection.reconnectAttempts++;
        await sleep(Temporal.Duration.from({ milliseconds: delay }));
        sinkPromise(connectLoop(escalationId));
      }
    };

    for (const escalationId of connections.keys()) {
      if (!subscribableIds.has(escalationId)) {
        stopConnection(escalationId);
      }
    }

    for (const escalationId of subscribableIds) {
      if (!connections.has(escalationId)) {
        connections.set(escalationId, {
          abortController: new AbortController(),
          messageIds: new Set(),
          reconnectAttempts: 0,
          connecting: false,
          everConnected: false,
          degradedReported: false,
        });
        sinkPromise(connectLoop(escalationId));
      }
    }
  }, [subscribableIdsKey, merchantRpcClient]);

  useEffect(() => {
    unmountedRef.current = false;
    const connections = connectionsRef.current;
    return () => {
      unmountedRef.current = true;
      for (const connection of connections.values()) {
        connection.abortController.abort();
      }
      connections.clear();
    };
  }, []);

  return { connectedEscalationIds };
}
